import React, { useState } from "react";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Button, Box, Typography
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { CustomerService } from "../../../service/customers.service";

interface Props {
    open: boolean;
    onClose: () => void;
    callback: () => void;   // called after a successful create so the parent can refresh its list
}

// Admin form to create a new enterprise. Mirrors the backend 'addEntManually' contract.
// IMPORTANT: the admin user's mobile must already exist as an ACTIVE retail user (two-step flow) —
// add the retail user first, then create the enterprise here. GST + EIN are required because both
// are UNIQUE columns in EnterpriseInfo (blank values would collide with each other).
const AddEnterpriseDialog = ({ open, onClose, callback }: Props) => {
    const customerService = new CustomerService();

    const [entname, setEntname] = useState("");
    const [mobileno, setMobileno] = useState("");
    const [entgst, setEntgst] = useState("");
    const [ein, setEin] = useState("");
    const [entalias, setEntalias] = useState("");
    const [entbranch, setEntbranch] = useState("");
    const [entaddress, setEntaddress] = useState("");
    const [saving, setSaving] = useState(false);

    const reset = () => {
        setEntname(""); setMobileno(""); setEntgst(""); setEin(""); setEntalias(""); setEntbranch(""); setEntaddress("");
    };
    const handleClose = () => { if (!saving) { reset(); onClose(); } };

    const submit = () => {
        const mobile = mobileno.trim();
        if (entname.trim() === "") {
            toast.error("Enterprise name is required", { position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000, hideProgressBar: true });
            return;
        }
        if (!/^[0-9]{10}$/.test(mobile)) {
            toast.error("Admin user's mobile must be 10 digits (and already an active retail user)", { position: toast.POSITION.BOTTOM_CENTER, autoClose: 3000, hideProgressBar: true });
            return;
        }
        if (entgst.trim() === "" || ein.trim() === "") {
            toast.error("GST number and EIN are required", { position: toast.POSITION.BOTTOM_CENTER, autoClose: 2500, hideProgressBar: true });
            return;
        }
        setSaving(true);
        customerService.addEnterprise(entname.trim(), entaddress.trim(), entbranch.trim(), entgst.trim().toUpperCase(), mobile, ein.trim(), entalias.trim()).then((res: any) => {
            setSaving(false);
            if (res != null && res.status == 100) {
                toast.success("Enterprise added successfully !!!", { position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000, hideProgressBar: true });
                reset();
                callback();
                onClose();
            } else {
                toast.error((res && res.msg) ? res.msg : "Action Failed !!!", { position: toast.POSITION.BOTTOM_CENTER, autoClose: 3000, hideProgressBar: true });
            }
        }).catch((err: any) => {
            setSaving(false);
            console.log('addEnterprise err ', err);
            toast.error("Network error while adding enterprise", { position: toast.POSITION.BOTTOM_CENTER, autoClose: 3000, hideProgressBar: true });
        });
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose} aria-labelledby="add-enterprise-title">
            <DialogTitle sx={{ m: 0, p: 2 }} id="add-enterprise-title">
                Add Enterprise
                <IconButton aria-label="close" onClick={handleClose}
                    sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    The admin user's mobile must already exist as an active retail user. Add the retail user first if needed.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField label="Enterprise Name" value={entname} onChange={(e) => setEntname(e.target.value)} fullWidth required />
                    <TextField label="Admin User Mobile" value={mobileno}
                        onChange={(e) => setMobileno(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                        fullWidth required inputProps={{ inputMode: 'numeric', maxLength: 10 }} helperText="Existing active retail user, 10 digits" />
                    <TextField label="GST Number" value={entgst} onChange={(e) => setEntgst(e.target.value.toUpperCase())} fullWidth required />
                    <TextField label="EIN" value={ein} onChange={(e) => setEin(e.target.value)} fullWidth required />
                    <TextField label="Alias" value={entalias} onChange={(e) => setEntalias(e.target.value)} fullWidth />
                    <TextField label="Branch" value={entbranch} onChange={(e) => setEntbranch(e.target.value)} fullWidth />
                    <TextField label="Address" value={entaddress} onChange={(e) => setEntaddress(e.target.value)} fullWidth multiline minRows={2} />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>Cancel</Button>
                <Button onClick={submit} variant="contained" disabled={saving}>{saving ? "Saving..." : "Add Enterprise"}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddEnterpriseDialog;
